import { MAP_DEFAULT_CENTER } from '../../config';
import {
  TaskConfig,
  TaskInfo,
  Trip,
  TouchPoint,
  EditPerformance,
  GeoBarrier,
  BreachesAndRatios,
  TripCostPerformance,
  TripMeta,
  AssignmentMeta,
} from '../../models/types';
import { RoutingJobResponse, RoutingJobViewResponse, routingJobsApi } from '../../api/routingJobsApi';
import { VerifyPayload, OrderBundle, DriverInfo, VehicleInfo, EquipmentInfo, OriginInfo } from '../../api/wmService';

type AnyRecord = Record<string, any>;

const DEFAULT_EDIT_PERFORMANCE: EditPerformance = {
  editPercent: 0,
  tpPerTrip: 0,
  wtPerTripKg: 0,
  distPerTripKm: 0,
  tpsWithoutTrip: 0,
  noOfTrips: 0,
  cpk: 0,
  cpTp: 0,
  tripsWithTimeBreach: 0,
  noOfSlotSlippage: 0,
  tpPerTripDelta: 0,
  wtPerTripKgDelta: 0,
  distPerTripKmDelta: 0,
  tpsWithoutTripDelta: 0,
  noOfTripsDelta: 0,
  cpkDelta: 0,
  cpTpDelta: 0,
  tripsWithTimeBreachDelta: 0,
  noOfSlotSlippageDelta: 0,
  ctoGmv: 0,
  ctoGmvDelta: 0,
};

export function adaptReworldTaskInfo(
  depotId: string,
  jobId: string,
  summaryView: RoutingJobViewResponse,
  tripsView: RoutingJobViewResponse
): TaskInfo {
  const summary = summaryView.summary ?? {};
  const editPerformance = normalizeEditPerformance(summary.editPerformance);
  const trips = (tripsView.items ?? []).map(mapTrip);
  const config = buildTaskConfig(depotId, summary, trips);

  return {
    taskId: jobId,
    userId: summary.createdBy ?? '',
    tenant: summary.tenantId ?? '',
    status: summary.status ?? 'COMPLETED',
    config,
    editPerf: editPerformance,
    trips,
  };
}

function buildTaskConfig(depotId: string, summary: AnyRecord, trips: Trip[]): TaskConfig {
  const center = findMapCenter(trips);
  const geoBarriers: GeoBarrier[] = [];

  return {
    id: depotId,
    algoType: summary.modelType ?? 'ROUTING',
    sourceLngLat: center,
    geoBarriers,
  };
}

function findMapCenter(trips: Trip[]): [number, number] {
  for (const trip of trips) {
    const center = trip.geoMarkers?.tripCenterLngLat;
    if (Array.isArray(center) && center.length === 2) {
      return [center[0], center[1]];
    }
  }
  return MAP_DEFAULT_CENTER;
}

function normalizeEditPerformance(raw: AnyRecord | undefined): EditPerformance {
  if (!raw) {
    return DEFAULT_EDIT_PERFORMANCE;
  }
  const units = typeof raw.units === 'object' ? raw.units : undefined;
  return {
    editPercent: toNumber(raw.editPercent),
    tpPerTrip: toNumber(raw.tpPerTrip),
    wtPerTripKg: toNumber(raw.wtPerTripKg),
    distPerTripKm: toNumber(raw.distPerTripKm),
    tpsWithoutTrip: toNumber(raw.tpsWithoutTrip),
    noOfTrips: toNumber(raw.noOfTrips),
    cpk: toNumber(raw.cpk),
    cpTp: toNumber(raw.cpTp),
    tripsWithTimeBreach: toNumber(raw.tripsWithTimeBreach),
    noOfSlotSlippage: toNumber(raw.noOfSlotSlippage),
    tpPerTripDelta: toNumber(raw.tpPerTripDelta),
    wtPerTripKgDelta: toNumber(raw.wtPerTripKgDelta),
    distPerTripKmDelta: toNumber(raw.distPerTripKmDelta),
    tpsWithoutTripDelta: toNumber(raw.tpsWithoutTripDelta),
    noOfTripsDelta: toNumber(raw.noOfTripsDelta),
    cpkDelta: toNumber(raw.cpkDelta),
    cpTpDelta: toNumber(raw.cpTpDelta),
    tripsWithTimeBreachDelta: toNumber(raw.tripsWithTimeBreachDelta),
    noOfSlotSlippageDelta: toNumber(raw.noOfSlotSlippageDelta),
    ctoGmv: toNumber(raw.ctoGmv),
    ctoGmvDelta: toNumber(raw.ctoGmvDelta),
    units,
  };
}

function mapTrip(item: AnyRecord): Trip {
  const geoMarkers = item.geoMarkers ?? {};
  const breaches = fillBreaches(item.breachesAndRatios);
  const tripCostPerf = fillCostPerformance(item.tripCostPerf);
  const tpDetails = Array.isArray(item.tpDetails) ? item.tpDetails.map(mapTouchPoint) : [];
  const tripMeta = mapTripMeta(item.tripMeta);
  const assignmentMeta = mapAssignmentMeta(item.assignmentMeta);

  return {
    name: String(item.name ?? 'Load'),
    vehTripNo: Number(item.vehTripNo ?? 0),
    distKm: toNumber(item.distKm),
    totalWtKg: toNumber(item.totalWtKg),
    volCuFt: toNumber(item.volCuFt),
    slotTags: item.slotTags ?? [],
    geoMarkers: {
      tripBoundaryLngLats: geoMarkers.tripBoundaryLngLats ?? [],
      tripCenterLngLat: geoMarkers.tripCenterLngLat ?? MAP_DEFAULT_CENTER,
      tripPolylineLngLats: geoMarkers.tripPolylineLngLats,
    },
    breachesAndRatios: breaches,
    tripCostPerf,
    vehDetails: {
      vehId: item.vehDetails?.vehId ?? '',
      driverName: item.vehDetails?.driverName ?? '',
      contractId: item.vehDetails?.contractId ?? '',
      contractType: item.vehDetails?.contractType ?? '',
    },
    tpDetails,
    sortValues: normalizeSortValues(item.sortValues),
    tripMeta,
    assignmentMeta,
  };
}

function mapTouchPoint(tp: AnyRecord): TouchPoint {
  const lngLat = Array.isArray(tp.lngLat) ? tp.lngLat : [0, 0];
  const attempt = tp.attemptTime ?? {};
  const openHours = Array.isArray(tp.openHoursMinOfDay) ? tp.openHoursMinOfDay : [{ start: 0, end: 1440 }];
  const itemSummary = tp.itemSummary ?? {};

  return {
    tpId: String(tp.tpId ?? ''),
    seqNo: Number(tp.seqNo ?? 0),
    name: String(tp.name ?? ''),
    address: tp.address ?? '',
    zipCode: tp.zipCode ?? '',
    lngLat: [toNumber(lngLat[0]), toNumber(lngLat[1])],
    attemptTime: {
      start: toNumber(attempt.start),
      end: toNumber(attempt.end),
    },
    openHoursMinOfDay: openHours.map((window: AnyRecord) => ({
      start: Number(window.start ?? 0),
      end: Number(window.end ?? 1440),
    })),
    slotTags: tp.slotTags ?? [],
    timeBreachMin: toNumber(tp.timeBreachMin),
    itemSummary: {
      dropWtKg: toNumber(itemSummary.dropWtKg),
      pickWtKg: toNumber(itemSummary.pickWtKg),
      volCuFt: toNumber(itemSummary.volCuFt),
      gmv: toNumber(itemSummary.gmv),
      dropItemCount: toNumber(itemSummary.dropItemCount),
      pickItemCount: toNumber(itemSummary.pickItemCount),
      distinctItemTags: itemSummary.distinctItemTags ?? [],
    },
    accuracyLevel: tp.accuracyLevel,
    orderNumber: tp.orderNumber ?? tp.salesOrderNumber,
    customerName: tp.customerName,
    generatorName: tp.generatorName,
    isHazmat: Boolean(tp.isHazmat),
    materialDescription: tp.materialDescription,
    scheduledDateTime: tp.scheduledDateTime,
    geoCorrectionRef: tp.tpId ? String(tp.tpId) : undefined,
    geoCorrectionRole: Array.isArray(tp.slotTags) && tp.slotTags.includes('PICKUP')
      ? 'PICKUP'
      : Array.isArray(tp.slotTags) && tp.slotTags.includes('DROP')
        ? 'DROP'
        : 'ORIGIN',
    geoCorrectionSupported: Array.isArray(tp.slotTags)
      ? tp.slotTags.includes('PICKUP') || tp.slotTags.includes('DROP')
      : false,
  };
}

function fillBreaches(raw: AnyRecord | undefined): BreachesAndRatios {
  const source = raw ?? {};
  return {
    wtBreachKg: toNumber(source.wtBreachKg),
    volBreachCuFt: toNumber(source.volBreachCuFt),
    distBreachKm: toNumber(source.distBreachKm),
    tpBreach: toNumber(source.tpBreach),
    timeBreachMin: toNumber(source.timeBreachMin),
    slotBreaches: toNumber(source.slotBreaches),
    wtToLimit: toNumber(source.wtToLimit),
    volToLimit: toNumber(source.volToLimit),
    distToLimit: toNumber(source.distToLimit),
    tpToLimit: toNumber(source.tpToLimit),
    timeToLimit: toNumber(source.timeToLimit),
    distinctZipCodes: source.distinctZipCodes ?? [],
  };
}

function fillCostPerformance(raw: AnyRecord | undefined): TripCostPerformance {
  const source = raw ?? {};
  return {
    cost: toNumber(source.cost),
    cpk: toNumber(source.cpk),
    cpt: toNumber(source.cpt),
    gmv: toNumber(source.gmv),
    ctoGmv: toNumber(source.ctoGmv),
  };
}

function mapTripMeta(raw: AnyRecord | undefined): TripMeta | undefined {
  if (!raw) return undefined;
  return {
    status: String(raw.status ?? 'SCHEDULED'),
    loadId: raw.loadId ? String(raw.loadId) : undefined,
    missingStops: Boolean(raw.missingStops),
    assignmentCount: toNumber(raw.assignmentCount),
    startEpoch: toNumber(raw.startEpoch),
    endEpoch: toNumber(raw.endEpoch),
  };
}

function mapAssignmentMeta(raw: AnyRecord | undefined): AssignmentMeta | undefined {
  if (!raw) return undefined;
  return {
    routes: Array.isArray(raw.routes) ? raw.routes.map(String) : [],
    driveDistanceMiles: toNumber(raw.driveDistanceMiles),
    tripDurationMinutes: toNumber(raw.tripDurationMinutes),
    originStartMinutes: toNumber(raw.originStartMinutes),
    trailerSourceIds: Array.isArray(raw.trailerSourceIds) ? raw.trailerSourceIds.map(String) : [],
    assignmentCount: toNumber(raw.assignmentCount),
  };
}

function normalizeSortValues(raw: AnyRecord | undefined): Record<string, number | string> | undefined {
  if (!raw) return undefined;
  const normalized: Record<string, number | string> = {};
  Object.entries(raw).forEach(([key, value]) => {
    if (typeof value === 'number') {
      normalized[key] = value;
      return;
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      normalized[key] = Number.isFinite(parsed) ? parsed : value;
      return;
    }
    if (typeof value === 'boolean') {
      normalized[key] = value ? 1 : 0;
      return;
    }
    if (value != null && typeof (value as any).toString === 'function') {
      normalized[key] = (value as any).toString();
    }
  });
  return normalized;
}

function toNumber(value: any): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export async function fetchReworldVerifyPayload(depotId: string, jobId: string): Promise<VerifyPayload> {
  const workspace = await routingJobsApi.getVerifyWorkspace(depotId, jobId);
  const views = Object.fromEntries((workspace.views ?? []).map((view) => [view.viewId, view]));
  return adaptReworldVerifyPayload(workspace.job, views);
}

export function adaptReworldVerifyPayload(
  job: RoutingJobResponse,
  views: Record<string, RoutingJobViewResponse | undefined>
): VerifyPayload {
  const ordersView = views.verify_orders;
  const driversView = views.verify_drivers;
  const vehiclesView = views.verify_vehicles;
  const trailersView = views.verify_trailers;

  const orders = mapVerifyOrdersFromView(ordersView?.items ?? []);
  if (!orders.length) {
    throw new Error('No orders available for verification');
  }
  const origin = deriveOrigin(job.depotSnapshot?.name, orders);
  const drivers = buildVerifyDrivers(driversView?.items ?? []);
  const fleet = buildVerifyFleet(vehiclesView?.items ?? []);
  const equipment = buildVerifyEquipment(trailersView?.items ?? [], origin);
  const lookups = buildLookups(origin, orders);

  return {
    runId: job.id,
    origin,
    orders,
    drivers,
    fleet,
    equipment,
    lookups,
  };
}

export function mapVerifyOrdersFromView(items: AnyRecord[]): OrderBundle[] {
  return items.map((item, index) => {
    const soId = item.salesOrder || item.orderUid || `SO-${index + 1}`;
    const pickupLat = parseNumber(item.pickupLat);
    const pickupLng = parseNumber(item.pickupLng);
    const dropLat = parseNumber(item.dropLat);
    const dropLng = parseNumber(item.dropLng);
    return {
      soId,
      fromTime: item.scheduledDateTime || '',
      toTime: item.scheduledDateTime || '',
      wasteType: item.materialDescription || '',
      qty: 0,
      uom: 'units',
      hazMat: truthy(item.hazardous) ? 'Yes' : 'No',
      requiredAsset: '',
      pickup: {
        id: `PICKUP-${soId}`,
        lat: pickupLat ?? dropLat ?? MAP_DEFAULT_CENTER[1],
        lng: pickupLng ?? dropLng ?? MAP_DEFAULT_CENTER[0],
        address: item.pickupLocation || '',
      },
      drop: {
        id: `DROP-${soId}`,
        lat: dropLat ?? pickupLat ?? MAP_DEFAULT_CENTER[1],
        lng: dropLng ?? pickupLng ?? MAP_DEFAULT_CENTER[0],
        address: item.dropLocation || '',
      },
      pickupGeoCorrection: {
        ref: item.orderUid || soId,
        role: 'PICKUP',
        editable: true,
      },
      dropGeoCorrection: {
        ref: item.orderUid || soId,
        role: 'DROP',
        editable: true,
      },
    };
  });
}

function buildVerifyDrivers(items: AnyRecord[]): DriverInfo[] {
  return items.map((item, index) => ({
    driverId: item.driverUid || `DRV-${index + 1}`,
    name: item.name || 'Driver',
    class: item.carrier || '',
    available: String(item.status || '').toLowerCase() !== 'inactive',
    shiftStartTime: '',
    shiftEndTime: '',
    licenceClasses: [],
    allowedVehicleTypes: [],
    skillTags: item.endorsements ? [String(item.endorsements)] : [],
  }));
}

function buildVerifyFleet(items: AnyRecord[]): VehicleInfo[] {
  return items.map((item, index) => ({
    vehicleId: item.vehicleUid || `VEH-${index + 1}`,
    name: item.name || 'Vehicle',
    capacity: 0,
    uom: 'kg',
    itemType: item.type || '',
    licenseRequired: item.ownershipType || '',
    assetType: item.type || '',
    tractorNeeded: 'No',
    tractorId: null,
  }));
}

function buildVerifyEquipment(items: AnyRecord[], origin: OriginInfo): EquipmentInfo[] {
  return items.map((item, index) => ({
    equipmentId: item.trailerUid || `EQ-${index + 1}`,
    fleetNumber: item.name || '',
    type: item.type || 'Trailer',
    wasteTypeAllowed: [],
    length_m: 0,
    width_m: 0,
    height_m: 0,
    capacity_volume_m3: 0,
    capacity_weight_kg: 0,
    lift_loader_type: '',
    compatible_container_types: [],
    service_time_per_stop_min: 0,
    max_stops_per_trip: 0,
    fuel_type: 'Diesel',
    fuel_range_km: 0,
    refuel_time_min: 0,
    refuel_cost: '',
    lat: parseNumber(item.domicileLat) ?? origin.lat,
    lng: parseNumber(item.domicileLng) ?? origin.lng,
    available: true,
  }));
}

function deriveOrigin(depotName: string | undefined, orders: OrderBundle[]): OriginInfo {
  const fallback = orders[0];
  return {
    originId: depotName || 'Depot',
    name: depotName || 'Depot',
    lat: fallback?.pickup.lat ?? MAP_DEFAULT_CENTER[1],
    lng: fallback?.pickup.lng ?? MAP_DEFAULT_CENTER[0],
    zip: '',
  };
}

function buildLookups(origin: OriginInfo, orders: OrderBundle[]) {
  const clients: Record<string, { clientId: string; address: string; lat: number; lng: number; hourStart: string; hourEnd: string }> = {};
  const drops: Record<string, { dropId: string; address: string; lat: number; lng: number; hourStart: string; hourEnd: string }> = {};

  orders.forEach((order) => {
    clients[order.pickup.id] = {
      clientId: order.pickup.id,
      address: order.pickup.address,
      lat: order.pickup.lat,
      lng: order.pickup.lng,
      hourStart: '00:00',
      hourEnd: '23:59',
    };
    drops[order.drop.id] = {
      dropId: order.drop.id,
      address: order.drop.address,
      lat: order.drop.lat,
      lng: order.drop.lng,
      hourStart: '00:00',
      hourEnd: '23:59',
    };
  });

  return {
    origin: {
      originId: origin.originId,
      address: origin.name,
      zip: String(origin.zip ?? ''),
      lat: origin.lat,
      lng: origin.lng,
      hourStart: '00:00',
      hourEnd: '23:59',
    },
    clients,
    drops,
  };
}

function parseNumber(value: any): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function truthy(value: any): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
  return false;
}
